import {InitPageStrict} from "../../index";
import {ShippingApiService} from "./ShippingApiService";
import {ShippingUI} from "./ShippingUI";
import {ShippingModals} from "./ShippingModals";
import {ShippingErrorHandler} from "./ShippingErrorHandler";
import {ShippingFormValidator} from "./ShippingFormValidator";
import {ShippingStorage} from "./ShippingStorage";
import {ShippingUrlManager} from "./ShippingUrlManager";
import {ShippingActions} from "./ShippingActions";
import {ShippingWorkflow} from "./ShippingWorkflow";
import {ShippingEventHandlers, EventHandlerCallbacks} from "./ShippingEventHandlers";
import {ShippingConfirmations} from "./ShippingConfirmations";
import * as Toastr from "toastr";

declare var autoShippedWhenOpeningSO: boolean;
declare var autoShipMultiSOItems: boolean;
declare var isViewOnly: boolean;

class WOPShippingIndex {
    private ui = new ShippingUI();
    private actions = new ShippingActions(this.ui);
    private workflow = new ShippingWorkflow(this.actions);
    private eventHandlers = new ShippingEventHandlers();

    constructor() {
        this.initialize();
    }

    private initialize(): void {
        this.eventHandlers.bindAll({
            onSearch: () => this.search(),
            onProcess: () => this.process(),
            onShopifyClick: (row, shopifyUrl) => this.handleShopifyAction(row, shopifyUrl),
            onShipClick: (row, asset) => this.confirmAndShip(row, asset),
            onAutoPopupChange: () => this.saveAutoPopupState()
        });

        this.loadAutoPopupState();
        this.checkQueryStringAndLoad();
    }

    public async search(): Promise<void> {
        const barcodeInput = this.eventHandlers.getBarcodeInput();
        const barcode = ShippingFormValidator.validateBarcode(barcodeInput.val() as string);

        if (!barcode) {
            return;
        }

        ShippingUrlManager.updateQueryString("so_barcode", barcode);

        try {
            const data = await ShippingApiService.fetchTableData(barcode);
            if (data.success) {
                this.ui.updateTable(data.assets || []);

                // Show missing items if they exist
                if (data.missing_items && data.missing_items.length > 0) {
                    this.ui.updateMissingItems(data.missing_items);
                } else {
                    this.ui.hideMissingItems();
                }

                setTimeout(() => {
                    this.eventHandlers.getEpcInput().focus();
                }, 100);
            } else {
                ShippingErrorHandler.showWarning(data.message || "No assets found");
                this.ui.updateTable([]);
                this.ui.hideMissingItems();
            }
        } catch (error) {
            ShippingErrorHandler.logError("fetching table data", error);
            const errorMessage = ShippingErrorHandler.handleApiError(
                error,
                "Error loading table data. Please try again."
            );
            ShippingErrorHandler.showError(errorMessage);
            this.ui.updateTable([]);
            this.ui.hideMissingItems();
        }
    }

    public async process(): Promise<void> {
        const epcInput = this.eventHandlers.getEpcInput();
        const epc = ShippingFormValidator.validateEpc(epcInput.val() as string);

        if (!epc) {
            return;
        }

        // Clear the table and barcode input
        this.ui.updateTable([]);
        this.eventHandlers.getBarcodeInput().val("");

        try {
            const data = await ShippingApiService.fetchAssetByEpc(epc);

            if (!data.success || !data.asset) {
                ShippingErrorHandler.showError(data.message || "Asset not found");
                epcInput.val("");
                return;
            }

            // Check if mode is SO multi
            const isSoMulti = data.wave_order && data.wave_order.is_so_multi === true;

            if (isSoMulti) {
                // In SO multi mode, load all assets (shipped or picked) into the table
                if (data.wave_order_assets && data.wave_order_assets.length > 0) {
                    const allAssets = data.wave_order_assets.map((asset: any) => ({
                        ...asset,
                        shopify_url: asset.shopify_url || data.shopify_url
                    }));

                    // Update table with all assets
                    this.ui.updateTable(allAssets);
                } else {
                    // If no wave_order_assets available, still add the entered asset
                    this.ui.upsertRow(data.asset, data.shopify_url);
                }

                // Show missing items if they exist (always check in SO multi mode)
                if (data.missing_items && data.missing_items.length > 0) {
                    this.ui.updateMissingItems(data.missing_items);
                } else {
                    this.ui.hideMissingItems();
                }
            } else {
                // Non-SO multi mode: only add the entered asset
                this.ui.upsertRow(data.asset, data.shopify_url);
                this.ui.hideMissingItems();
            }

            await this.handleWorkflow(data);
            epcInput.val("");
        } catch (error) {
            ShippingErrorHandler.logError("fetching asset details", error);
            const errorMessage = ShippingErrorHandler.handleApiError(
                error,
                "Error loading asset details. Please try again."
            );
            ShippingErrorHandler.showError(errorMessage);
            epcInput.val("");
        }
    }

    private async handleWorkflow(data: any): Promise<void> {
        const autoPopup = this.eventHandlers.getAutoPopupCheckbox().is(":checked");

        await this.workflow.handleWorkflow(
            data,
            autoPopup,
            (data, onShopify) => ShippingModals.showSoMulti(
                data,
                onShopify,
                (assetId?: string | number, epc?: string) => this.actions.reloadAssetInTable(assetId, epc)
            ),
            (data, onShopify) => ShippingModals.showDetails(data, onShopify)
        );
    }

    private async handleShopifyAction(row: JQuery<HTMLElement>, shopifyUrl?: string): Promise<void> {
        const url = shopifyUrl || row.attr("data-shopify-url");

        if (!ShippingFormValidator.validateShopifyUrl(url)) {
            return;
        }

        const isShipped = row.attr("data-shipped") === "true";
        if (isShipped) {
            this.actions.openShopify(url);
            return;
        }

        // In view only mode, just open Shopify without shipping
        if (isViewOnly) {
            this.actions.openShopify(url);
            return;
        }

        // Check if Ship All option is enabled AND wave order is SO multi mode
        const shipAllEnabled = typeof autoShipMultiSOItems !== 'undefined' ? autoShipMultiSOItems : false;
        const isSoMulti = row.attr("data-is-so-multi") === "true";

        if (shipAllEnabled && isSoMulti) {
            // Ship All is enabled - check Auto Ship setting
            if (autoShippedWhenOpeningSO) {
                // Auto Ship enabled: Show confirmation, ship all assets, then open Shopify
                await this.confirmShopifyAndShipAll(row, url);
            } else {
                // Auto Ship disabled: Just open Shopify (no shipping)
                this.actions.openShopify(url);
            }
        } else {
            // Ship All disabled - use original behavior (ship single asset)
            if (autoShippedWhenOpeningSO) {
                await this.confirmShopifyAndShip(row, url);
            } else {
                this.actions.openShopify(url);
            }
        }
    }

    private async confirmShopifyAndShipAll(row: JQuery<HTMLElement>, shopifyUrl: string): Promise<void> {
        // Get the ref_number and asset ID/EPC from the clicked row
        const refNumber = row.attr("data-ref-number") || "";
        const assetId = row.attr("data-asset-id") || "";
        const epc = row.attr("data-epc") || "";
        const clickedAssetIdOrEpc = assetId || epc;

        // If ref_number is missing or invalid, fallback to shipping only the clicked asset
        if (!refNumber || refNumber === "" || refNumber === "-") {
            if (!clickedAssetIdOrEpc) {
                ShippingErrorHandler.showWarning("Cannot determine which asset to ship");
                this.actions.openShopify(shopifyUrl);
                return;
            }

            // Ship only the clicked asset
            await ShippingConfirmations.confirmShopifyAndShip(async () => {
                await this.actions.markAsShippedSilently(clickedAssetIdOrEpc);
                await this.actions.reloadAssetInTable(epc || assetId);
                this.actions.openShopify(shopifyUrl);
            });
            return;
        }

        // Get all assets from the table
        const allAssets = this.getAllAssetsFromTable();

        if (allAssets.length === 0) {
            ShippingErrorHandler.showWarning("No assets found in table");
            return;
        }

        // Filter assets by sales order (ref_number)
        // This ensures we only ship assets from the same sales order when "ship all" is enabled
        const filteredAssets = allAssets.filter(asset => asset.refNumber === refNumber);

        // Filter out already shipped assets
        const assetsToShip = filteredAssets.filter(asset => !asset.shipped);

        if (assetsToShip.length === 0) {
            ShippingErrorHandler.showWarning("All assets for this sales order are already shipped");
            this.actions.openShopify(shopifyUrl);
            return;
        }

        await ShippingConfirmations.confirmShopifyAndShip(async () => {
            // Ship all non-shipped assets
            const shippedEpcs = new Set<string>();
            for (const asset of assetsToShip) {
                try {
                    const assetId = asset.id ? parseInt(asset.id, 10) : undefined;
                    if (assetId) {
                        const response = await this.actions.markAsShippedSilently(assetId);
                        if (response && response.success) {
                            // Immediately update the row's shipped status
                            this.actions.updateRowShippedStatus(assetId, asset.epc);
                            if (asset.epc) {
                                shippedEpcs.add(asset.epc);
                            }
                        }
                    }
                } catch (error) {
                    ShippingErrorHandler.logError("marking asset as shipped", error);
                }
            }

            // Reload all assets with the same EPCs to ensure all are updated
            for (const epc of Array.from(shippedEpcs)) {
                await this.actions.reloadAllAssetsWithSameEpc(epc);
            }

            // Open Shopify after shipping all assets
            this.actions.openShopify(shopifyUrl);
        });
    }

    private getAllAssetsFromTable(): Array<{id: string, epc: string, shopifyUrl: string, shipped: boolean, refNumber: string}> {
        const assets: Array<{id: string, epc: string, shopifyUrl: string, shipped: boolean, refNumber: string}> = [];
        const tableBody = $("#assets-table");

        tableBody.find("tr").each(function() {
            const row = $(this);
            const assetId = row.attr("data-asset-id") || "";
            const epc = row.attr("data-epc") || "";
            const shopifyUrl = row.attr("data-shopify-url") || "";
            const shipped = row.attr("data-shipped") === "true";
            const refNumber = row.attr("data-ref-number") || "";

            if (assetId || epc) {
                assets.push({
                    id: assetId,
                    epc: epc,
                    shopifyUrl: shopifyUrl,
                    shipped: shipped,
                    refNumber: refNumber
                });
            }
        });

        return assets;
    }

    private async confirmShopifyAndShip(row: JQuery<HTMLElement>, shopifyUrl: string): Promise<void> {
        const assetIdStr = row.attr("data-asset-id");
        const epc = row.attr("data-epc");
        const assetId = assetIdStr ? parseInt(assetIdStr, 10) : undefined;

        if (!assetId && !epc) {
            ShippingErrorHandler.showError("Asset not found");
            return;
        }

        await ShippingConfirmations.confirmShopifyAndShip(async () => {
            if (assetId) {
                const response = await this.actions.markAsShippedSilently(assetId);
                if (response && response.success) {
                    // Immediately update the row's shipped status
                    this.actions.updateRowShippedStatus(assetId, epc);
                    this.actions.openShopify(shopifyUrl);
                    await this.actions.reloadAssetInTable(assetId, epc);
                }
            }
        });
    }

    private async confirmAndShip(row: JQuery<HTMLElement>, asset: any): Promise<void> {
        if (isViewOnly) {
            ShippingErrorHandler.showWarning("This page is view only.");
            return;
        }

        const assetId = row.attr("data-asset-id");
        const epc = row.attr("data-epc");

        if (!ShippingFormValidator.validateAsset({ id: assetId, epc })) {
            return;
        }

        const serialNumber = row.attr("data-serial-number") || epc || assetId || "this asset";
        const refNumber = row.attr("data-ref-number") || "";
        const isSoMulti = row.attr("data-is-so-multi") === "true";

        // Check if Ship All option is enabled AND wave order is SO multi mode
        const shipAllEnabled = typeof autoShipMultiSOItems !== 'undefined' ? autoShipMultiSOItems : false;

        if (shipAllEnabled && isSoMulti) {
            // Ship All is enabled AND wave order is SO multi - ship all non-shipped assets for the same sales order
            // If ref_number is missing, fallback to shipping only the clicked asset
            const assetIdOrEpc = assetId || epc;
            await this.confirmAndShipAll(serialNumber, refNumber, assetIdOrEpc);
        } else {
            // Ship All is disabled - ship only the clicked asset
            const assetIdNum = assetId ? parseInt(assetId, 10) : undefined;
            if (assetIdNum && epc) {
                await ShippingConfirmations.confirmMarkAsShipped(serialNumber, async () => {
                    const response = await this.actions.markAsShippedSilently(assetIdNum);
                    if (response && response.success) {
                        // Immediately update the row's shipped status for instant feedback
                        this.actions.updateRowShippedStatus(assetIdNum, epc);
                        // Then reload from server to ensure we have the latest data
                        await this.actions.reloadAssetInTable(assetIdNum, epc);
                        // Also reload all assets with the same EPC to ensure all are updated
                        await this.actions.reloadAllAssetsWithSameEpc(epc);
                    }
                });
            }
        }
    }

    private async confirmAndShipAll(serialNumber: string, refNumber?: string, clickedAssetIdOrEpc?: string): Promise<void> {
        // If ref_number is missing or invalid, fallback to shipping only the clicked asset
        if (!refNumber || refNumber === "" || refNumber === "-") {
            if (!clickedAssetIdOrEpc) {
                ShippingErrorHandler.showWarning("Cannot determine which asset to ship");
                return;
            }

            // Ship only the clicked asset
            await ShippingConfirmations.confirmMarkAsShipped(serialNumber, async () => {
                await this.actions.markAsShippedSilently(clickedAssetIdOrEpc);
                await this.actions.reloadAssetInTable(clickedAssetIdOrEpc);
            });
            return;
        }

        // Get all assets from the table
        const allAssets = this.getAllAssetsFromTable();

        if (allAssets.length === 0) {
            ShippingErrorHandler.showWarning("No assets found in table");
            return;
        }

        // Filter assets by sales order (ref_number)
        // This ensures we only ship assets from the same sales order when "ship all" is enabled
        const filteredAssets = allAssets.filter(asset => asset.refNumber === refNumber);

        // Filter out already shipped assets
        const assetsToShip = filteredAssets.filter(asset => !asset.shipped);

        if (assetsToShip.length === 0) {
            ShippingErrorHandler.showWarning("All assets for this sales order are already shipped");
            return;
        }

        // Show confirmation dialog with appropriate message
        const confirmMessage = assetsToShip.length === 1
            ? `Do you want to mark the asset with serial #${serialNumber} as shipped?`
            : `Do you want to mark all ${assetsToShip.length} non-shipped asset(s) for sales order ${refNumber} as shipped?`;

        // Use a custom confirmation since we need a custom message
        await new Promise<void>((resolve) => {
            (window as any).swal({
                title: 'Are you sure?',
                text: confirmMessage,
                icon: "warning",
                closeOnClickOutside: false,
                className: "sn-m-modal",
                buttons: {
                    cancel: {
                        text: "No",
                        value: null,
                        visible: true,
                        className: "sn-btn sn-btn-sm sn-btn-silver",
                        closeModal: true
                    },
                    confirm: {
                        text: "Yes",
                        value: true,
                        visible: true,
                        className: "sn-btn sn-btn-sm sn-btn-red",
                        closeModal: true
                    }
                },
                dangerMode: true,
            }).then(async (willMark: boolean) => {
                if (!willMark) {
                    resolve();
                    return;
                }

                // Ship all non-shipped assets
                const shippedEpcs = new Set<string>();
                for (const asset of assetsToShip) {
                    try {
                        const assetId = asset.id ? parseInt(asset.id, 10) : undefined;
                        if (assetId) {
                            const response = await this.actions.markAsShippedSilently(assetId);
                            if (response && response.success) {
                                // Immediately update the row's shipped status
                                this.actions.updateRowShippedStatus(assetId, asset.epc);
                                if (asset.epc) {
                                    shippedEpcs.add(asset.epc);
                                }
                            }
                        }
                    } catch (error) {
                        ShippingErrorHandler.logError("marking asset as shipped", error);
                    }
                }

                // Reload all assets with the same EPCs to ensure all are updated
                for (const epc of Array.from(shippedEpcs)) {
                    await this.actions.reloadAllAssetsWithSameEpc(epc);
                }

                Toastr.success(`${assetsToShip.length} asset(s) marked as shipped`, {
                    timeOut: 3000,
                    positionClass: 'toast-top-right'
                });
                resolve();
            });
        });
    }

    private checkQueryStringAndLoad(): void {
        ShippingUrlManager.checkQueryStringAndLoad("so_barcode", (barcode) => {
            this.eventHandlers.getBarcodeInput().val(barcode);
            this.search();
        });
    }

    private loadAutoPopupState(): void {
        const isChecked = ShippingStorage.loadAutoPopupState();
        if (isChecked) {
            this.eventHandlers.getAutoPopupCheckbox().prop("checked", true);
        }
    }

    private saveAutoPopupState(): void {
        const isChecked = this.eventHandlers.getAutoPopupCheckbox().is(":checked");
        ShippingStorage.saveAutoPopupState(isChecked);
    }
}

declare global {
    interface Window {
        WOPShippingIndex: WOPShippingIndex;
    }
}

export function LoadPage() {
    InitPageStrict("wop shippings index", () => {
        const wopShippingIndex = new WOPShippingIndex();
        window.WOPShippingIndex = wopShippingIndex;
    });
}
